import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft, MapPin, Phone, Mail, Users, Layers, Map, Crown, Flag, UserPlus, Pencil, ArrowUp, Building2, MessageSquare, ExternalLink, Calendar } from 'lucide-react'
import clsx from 'clsx'
import axios from 'axios'
import { MapContainer, TileLayer, GeoJSON, CircleMarker, Tooltip } from 'react-leaflet'
import type { Layer, LeafletMouseEvent } from 'leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { buildApiUrl } from '../../../config/api'
import { Card, Badge, EmptyState, SlidePanel } from '../../../components/ui'
import { formatPhone } from '../../../utils/masks'
import { formatDate, formatDateTime, formatRelativeDate } from '../../../utils/dateUtils'

interface CityData {
  city_id: number
  city_name: string
  city_ibge_id: number
  state_code: string
  state_id: number
  state_ibge_id: number
  voters_count: number
}

interface NeighborhoodData {
  neighborhood: string
  geo_city_id: number
  voters_count: number
}

interface ScopeData {
  scope: string
  state_id?: number
  state_ibge_id?: number
  state_code?: string
  city_id?: number
  city_ibge_id?: number
}

interface VoterPoint {
  id: number
  name: string
  latitude: number
  longitude: number
  neighborhood: string | null
}

interface MapData {
  total_voters: number
  voters_with_address: number
  voters_without_address: number
  voters_with_coords: number
  cities: CityData[]
  neighborhoods: NeighborhoodData[]
  voter_points: VoterPoint[]
  scope: ScopeData | null
  has_campaign: boolean
}

interface VoterDetail {
  id: number; name: string; nickname: string | null; cpf: string | null
  phone: string; email: string | null; birth_date: string | null; gender: string | null
  is_contact: boolean; is_voter: boolean
  zip_code: string | null; street: string | null; number: string | null
  neighborhood: string | null; complement: string | null
  city_name: string | null; state_code: string | null; neighborhood_name: string | null
  channel_label: string | null; form_name: string | null; leader_name: string | null
  voter_registration: string | null; electoral_zone: string | null; electoral_section: string | null
  status_code: string; status_label: string
  notes: string | null; created_at: string
}

interface HistoryItem {
  id: number; description: string | null; action_code: string; action_label: string
  action_icon: string; action_color: string
  old_type: string | null; new_type: string | null
  user_name: string | null; leader_name: string | null; channel_label: string | null
  created_at: string
}

interface CampaignItem {
  id: number; name: string; election_year: number; is_active_campaign: boolean
  status_label: string; engagement_active: boolean; engaged_at: string
}

type MapView = 'heat' | 'pins'

const iconMap: Record<string, React.ReactNode> = {
  UserPlus: <UserPlus className="w-3.5 h-3.5" />,
  Pencil: <Pencil className="w-3.5 h-3.5" />,
  ArrowUp: <ArrowUp className="w-3.5 h-3.5" />,
  Building2: <Building2 className="w-3.5 h-3.5" />,
  Phone: <Phone className="w-3.5 h-3.5" />,
  Flag: <Flag className="w-3.5 h-3.5" />,
  MessageSquare: <MessageSquare className="w-3.5 h-3.5" />,
}

export default function VotersMapPage() {
  const [data, setData] = useState<MapData | null>(null)
  const [filter, setFilter] = useState<'all' | 'campaign' | 'not_campaign'>('campaign')
  const [loading, setLoading] = useState(true)
  const [geoJson, setGeoJson] = useState<any>(null)
  const [cityNames, setCityNames] = useState<Record<string, string>>({})
  const [focusCityIbgeId, setFocusCityIbgeId] = useState<number | null>(null)
  const [mapView, setMapView] = useState<MapView>('heat')
  const [selectedVoter, setSelectedVoter] = useState<VoterDetail | null>(null)
  const [voterHistory, setVoterHistory] = useState<HistoryItem[]>([])
  const [voterCampaigns, setVoterCampaigns] = useState<CampaignItem[]>([])
  const [panelOpen, setPanelOpen] = useState(false)
  const [panelLoading, setPanelLoading] = useState(false)
  const [panelTab, setPanelTab] = useState<'dados' | 'atividade' | 'campanhas'>('dados')
  const navigate = useNavigate()
  const mapFitted = useRef(false)

  useEffect(() => { fetchMap() }, [filter])

  async function fetchMap() {
    setLoading(true)
    mapFitted.current = false
    try {
      const res = await axios.get(buildApiUrl('/v1/reports/voters/map'), { params: { filter } })
      setData(res.data.data)
      const scopeInfo = res.data.data.scope
      setFocusCityIbgeId(scopeInfo?.scope === 'MUNICIPAL' && scopeInfo?.city_ibge_id ? scopeInfo.city_ibge_id : null)
      if (scopeInfo?.state_id) {
        await loadGeoJson(scopeInfo.state_id)
      }
    } catch { /* */ } finally { setLoading(false) }
  }

  async function loadGeoJson(stateId: number) {
    try {
      const [geoRes, citiesRes] = await Promise.all([
        axios.get(buildApiUrl(`/v1/geo/states/${stateId}/boundaries`)),
        axios.get(buildApiUrl(`/v1/geo/states/${stateId}/cities`)),
      ])
      setGeoJson(geoRes.data)
      const names: Record<string, string> = {}
      citiesRes.data.data.forEach((c: any) => { names[String(c.ibge_id)] = c.name })
      setCityNames(names)
    } catch { /* */ }
  }

  async function openVoterPanel(voterId: number) {
    setPanelOpen(true)
    setPanelLoading(true)
    setPanelTab('dados')
    try {
      const res = await axios.get(buildApiUrl(`/v1/voters/${voterId}`))
      setSelectedVoter(res.data.data.voter)
      setVoterHistory(res.data.data.history || [])
      setVoterCampaigns(res.data.data.campaigns || [])
    } catch { /* */ } finally { setPanelLoading(false) }
  }

  function closePanel() {
    setPanelOpen(false)
    setSelectedVoter(null)
    setVoterHistory([])
    setVoterCampaigns([])
  }

  const votersByIbge: Record<number, CityData> = {}
  data?.cities?.forEach(c => { votersByIbge[c.city_ibge_id] = c })
  const maxCount = data?.cities?.length ? Math.max(...data.cities.map(c => c.voters_count), 1) : 1

  function getColor(count: number): string {
    if (count === 0) return '#e4e7ec'
    const ratio = count / maxCount
    if (ratio >= 0.8) return '#15803d'
    if (ratio >= 0.6) return '#22c55e'
    if (ratio >= 0.4) return '#4ade80'
    if (ratio >= 0.2) return '#86efac'
    return '#bbf7d0'
  }

  const styleFeature = useCallback((feature: any) => {
    const ibgeId = feature?.properties?.codarea
    const cityData = ibgeId ? votersByIbge[Number(ibgeId)] : null
    const isFocusCity = focusCityIbgeId && Number(ibgeId) === focusCityIbgeId

    if (mapView === 'pins') {
      return {
        fillColor: 'transparent',
        weight: isFocusCity ? 2 : 0.5,
        opacity: isFocusCity ? 1 : 0.3,
        color: isFocusCity ? '#22c55e' : '#9ca3af',
        fillOpacity: 0,
      }
    }

    return {
      fillColor: isFocusCity ? '#dcfce7' : getColor(cityData?.voters_count || 0),
      weight: isFocusCity ? 3 : 1,
      opacity: 1,
      color: isFocusCity ? '#22c55e' : 'white',
      fillOpacity: isFocusCity ? 0.4 : 0.85,
    }
  }, [data, focusCityIbgeId, mapView])

  const onEachFeature = useCallback((feature: any, layer: Layer) => {
    const ibgeId = feature?.properties?.codarea
    const cityData = ibgeId ? votersByIbge[Number(ibgeId)] : null
    const name = (ibgeId && cityNames[String(ibgeId)]) || 'Desconhecido'
    const count = cityData?.voters_count || 0

    layer.bindTooltip(`<strong>${name}</strong><br/>${count.toLocaleString('pt-BR')} eleitor${count !== 1 ? 'es' : ''}`, { sticky: true })

    layer.on({
      mouseover: (e: LeafletMouseEvent) => {
        e.target.setStyle({ weight: 2, color: '#22c55e', fillOpacity: 0.95 })
      },
      mouseout: (e: LeafletMouseEvent) => {
        const isFocus = focusCityIbgeId && Number(ibgeId) === focusCityIbgeId
        if (mapView === 'pins') {
          e.target.setStyle({ weight: isFocus ? 2 : 0.5, color: isFocus ? '#22c55e' : '#9ca3af', fillOpacity: 0 })
        } else {
          e.target.setStyle({ weight: isFocus ? 3 : 1, color: isFocus ? '#22c55e' : 'white', fillOpacity: isFocus ? 0.4 : 0.85 })
        }
      },
    })
  }, [data, cityNames, mapView, focusCityIbgeId])

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/reports/map')} className="h-9 w-9 flex items-center justify-center rounded-lg hover:bg-gray-200/60 text-gray-400 hover:text-gray-600 transition-colors">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Mapa de Eleitores</h1>
            <p className="text-gray-500 mt-1">Distribuição geográfica dos contatos</p>
          </div>
        </div>
        {data?.has_campaign && (
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            {(['all', 'campaign', 'not_campaign'] as const).map(f => (
              <button key={f} onClick={() => setFilter(f)} className={clsx('px-3 py-1.5 rounded-md text-sm font-medium transition-colors', filter === f ? 'bg-brand-50 text-brand-600 shadow-xs' : 'text-gray-500')}>
                {f === 'all' ? 'Todos' : f === 'campaign' ? 'Campanha' : 'Fora'}
              </button>
            ))}
          </div>
        )}
      </div>

      {loading ? (
        <Card>
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="w-8 h-8 border-4 border-brand-200 border-t-brand-500 rounded-full animate-spin mx-auto mb-3" />
              <p className="text-sm text-gray-500">Carregando...</p>
            </div>
          </div>
        </Card>
      ) : data && (!data.scope || (data.scope.scope !== 'NATIONAL' && !data.scope.state_id)) ? (
        <Card>
          <EmptyState
            icon={<MapPin className="w-12 h-12" />}
            title="Abrangência incompleta"
            description="Configure a abrangência da organização em Configurações > Político para visualizar o mapa."
          />
        </Card>
      ) : data ? (<>
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { v: data.total_voters.toLocaleString('pt-BR'), l: 'Contatos' },
            { v: data.voters_with_address.toLocaleString('pt-BR'), l: 'Com endereco' },
            { v: data.cities.length, l: 'Cidades' },
            { v: data.voters_without_address.toLocaleString('pt-BR'), l: 'Sem endereco', warn: true },
          ].map(s => (
            <div key={s.l} className="bg-white rounded-2xl border border-gray-200 p-4 text-center">
              <p className={clsx('text-2xl font-semibold', s.warn ? 'text-warning-500' : 'text-gray-900')}>{s.v}</p>
              <p className="text-xs text-gray-500 mt-1">{s.l}</p>
            </div>
          ))}
        </div>

        {/* Toggle */}
        <div className="flex items-center justify-end">
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            <button onClick={() => setMapView('heat')} className={clsx('px-3 py-1 rounded-md text-xs font-medium transition-colors flex items-center gap-1.5', mapView === 'heat' ? 'bg-white text-gray-800 shadow-xs' : 'text-gray-500')}>
              <Layers className="w-3.5 h-3.5" />Calor
            </button>
            <button onClick={() => setMapView('pins')} className={clsx('px-3 py-1 rounded-md text-xs font-medium transition-colors flex items-center gap-1.5', mapView === 'pins' ? 'bg-white text-gray-800 shadow-xs' : 'text-gray-500')}>
              <Map className="w-3.5 h-3.5" />Mapa
            </button>
          </div>
        </div>

        {/* Mapa */}
        <Card padding="none">
          <div style={{ height: 'min(500px, 60vh)' }} className="rounded-2xl overflow-hidden">
            {geoJson ? (
              <MapContainer center={[-14.24, -51.93]} zoom={4} style={{ height: '100%', width: '100%' }} scrollWheelZoom={true} ref={mapRef => {
                if (mapRef && geoJson && !mapFitted.current) {
                  mapFitted.current = true
                  setTimeout(() => {
                    try {
                      if (focusCityIbgeId && geoJson.features) {
                        const cityFeature = geoJson.features.find((f: any) => Number(f.properties?.codarea) === focusCityIbgeId)
                        if (cityFeature) {
                          const cityBounds = L.geoJSON(cityFeature).getBounds()
                          if (cityBounds.isValid()) { mapRef.fitBounds(cityBounds, { padding: [40, 40] }); return }
                        }
                      }
                      const bounds = L.geoJSON(geoJson).getBounds()
                      if (bounds.isValid()) mapRef.fitBounds(bounds, { padding: [20, 20] })
                    } catch { /* */ }
                  }, 100)
                }
              }}>
                <TileLayer
                  attribution='&copy; CARTO'
                  url={mapView === 'pins'
                    ? 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png'
                    : 'https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png'
                  }
                />
                <GeoJSON key={mapView + Object.keys(cityNames).length} data={geoJson} style={styleFeature} onEachFeature={onEachFeature} />
                {mapView === 'pins' && data?.voter_points?.map(p => (
                  <CircleMarker
                    key={p.id}
                    center={[p.latitude, p.longitude]}
                    radius={6}
                    pathOptions={{ fillColor: '#7c3aed', fillOpacity: 0.8, color: '#6d28d9', weight: 1.5 }}
                    eventHandlers={{ click: () => openVoterPanel(p.id) }}
                  >
                    <Tooltip>{p.name}{p.neighborhood ? ` - ${p.neighborhood}` : ''}</Tooltip>
                  </CircleMarker>
                ))}
              </MapContainer>
            ) : null}
          </div>
        </Card>

        {/* Legenda */}
        {mapView === 'heat' && (
          <div className="flex items-center justify-center gap-4 text-xs text-gray-500">
            {[['#e4e7ec','Sem presença'],['#bbf7d0','Baixo'],['#86efac','Médio'],['#22c55e','Alto'],['#15803d','Muito alto']].map(([c,l]) => (
              <span key={l} className="flex items-center gap-1.5"><span className="w-3 h-3 rounded" style={{ background: c }} />{l}</span>
            ))}
          </div>
        )}

        {/* Ranking por cidade */}
        {data.cities.length > 0 && (
          <Card>
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Ranking por cidade</h3>
            <div className="space-y-2">
              {data.cities.map((city, i) => (
                <div key={city.city_id} className="flex items-center gap-3 p-2 -mx-2">
                  <span className="w-6 text-right text-xs font-bold text-gray-400">{i + 1}</span>
                  <span className="w-32 text-left text-sm font-medium text-gray-800 truncate">{city.city_name}/{city.state_code}</span>
                  <div className="flex-1 h-6 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${Math.max((city.voters_count / maxCount) * 100, 8)}%`, backgroundColor: getColor(city.voters_count) }} />
                  </div>
                  <span className="text-sm font-semibold text-gray-800 w-16 text-right">{city.voters_count.toLocaleString('pt-BR')}</span>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Ranking por bairro */}
        {data.neighborhoods.length > 0 && (
          <Card>
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Top bairros</h3>
            <div className="space-y-2">
              {data.neighborhoods.map((nb, i) => {
                const maxNb = data.neighborhoods[0]?.voters_count || 1
                return (
                  <div key={`${nb.neighborhood}-${nb.geo_city_id}`} className="flex items-center gap-3 p-2 -mx-2">
                    <span className="w-6 text-right text-xs font-bold text-gray-400">{i + 1}</span>
                    <span className="w-40 text-left text-sm font-medium text-gray-800 truncate">{nb.neighborhood}</span>
                    <div className="flex-1 h-6 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full bg-emerald-400" style={{ width: `${Math.max((nb.voters_count / maxNb) * 100, 8)}%` }} />
                    </div>
                    <span className="text-sm font-semibold text-gray-800 w-16 text-right">{nb.voters_count.toLocaleString('pt-BR')}</span>
                  </div>
                )
              })}
            </div>
          </Card>
        )}
      </>) : null}

      {/* SlidePanel - Detalhes do eleitor */}
      <SlidePanel
        open={panelOpen}
        onClose={closePanel}
        title={selectedVoter?.name || 'Carregando...'}
        subtitle={selectedVoter ? (selectedVoter.is_voter ? 'Eleitor' : 'Contato') : undefined}
        size="lg"
        actions={selectedVoter ? (
          <button
            onClick={() => navigate(selectedVoter.is_voter ? `/voters/${selectedVoter.id}` : `/contacts/${selectedVoter.id}`)}
            className="h-8 px-3 flex items-center gap-1.5 rounded-lg text-xs font-medium text-brand-600 hover:bg-brand-50 transition-colors"
          >
            <ExternalLink className="w-3.5 h-3.5" /> Ver perfil completo
          </button>
        ) : undefined}
      >
        {panelLoading ? (
          <div className="p-6 space-y-4">
            <div className="space-y-3">
              {[1,2,3,4,5].map(i => (
                <div key={i} className="flex gap-3">
                  <div className="h-3 w-20 bg-gray-100 rounded animate-pulse" />
                  <div className="h-3 w-40 bg-gray-100 rounded animate-pulse" />
                </div>
              ))}
            </div>
            <div className="h-px bg-gray-100 my-4" />
            <div className="space-y-3">
              {[1,2,3].map(i => <div key={i} className="h-12 bg-gray-50 rounded-xl animate-pulse" />)}
            </div>
          </div>
        ) : selectedVoter ? (
          <div>
            {/* Tabs */}
            <div className="flex gap-1 bg-gray-50 p-1 mx-6 mt-4 rounded-lg">
              {([
                { id: 'dados' as const, label: 'Dados' },
                { id: 'atividade' as const, label: `Atividade${voterHistory.length ? ` (${voterHistory.length})` : ''}` },
                { id: 'campanhas' as const, label: `Campanhas${voterCampaigns.length ? ` (${voterCampaigns.length})` : ''}` },
              ]).map(tab => (
                <button key={tab.id} onClick={() => setPanelTab(tab.id)} className={clsx('flex-1 px-3 py-1.5 rounded-md text-xs font-medium transition-colors', panelTab === tab.id ? 'bg-white text-gray-900 shadow-xs' : 'text-gray-500 hover:text-gray-700')}>
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Tab: Dados */}
            {panelTab === 'dados' && (
              <div className="p-6 space-y-5">
                {/* Info principal */}
                <div className="space-y-3">
                  <InfoRow icon={<Phone className="w-3.5 h-3.5" />} label="Telefone" value={formatPhone(selectedVoter.phone)} />
                  {selectedVoter.email && <InfoRow icon={<Mail className="w-3.5 h-3.5" />} label="Email" value={selectedVoter.email} />}
                  {selectedVoter.nickname && <InfoRow label="Apelido" value={selectedVoter.nickname} />}
                  {selectedVoter.cpf && <InfoRow label="CPF" value={selectedVoter.cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')} />}
                  {selectedVoter.birth_date && <InfoRow icon={<Calendar className="w-3.5 h-3.5" />} label="Nascimento" value={formatDate(selectedVoter.birth_date)} />}
                  {selectedVoter.gender && <InfoRow label="Genero" value={selectedVoter.gender === 'M' ? 'Masculino' : selectedVoter.gender === 'F' ? 'Feminino' : 'Outro'} />}
                </div>

                {/* Endereco */}
                {(selectedVoter.street || selectedVoter.neighborhood || selectedVoter.city_name) && (
                  <div className="border-t border-gray-100 pt-4">
                    <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Endereco</h4>
                    <div className="flex items-start gap-2">
                      <MapPin className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div className="text-sm text-gray-700">
                        {[selectedVoter.street, selectedVoter.number].filter(Boolean).join(', ')}
                        {selectedVoter.complement && ` - ${selectedVoter.complement}`}
                        {selectedVoter.zip_code && ` - CEP ${selectedVoter.zip_code}`}
                        {(selectedVoter.neighborhood_name || selectedVoter.neighborhood) && (
                          <p className="text-gray-500">{selectedVoter.neighborhood_name || selectedVoter.neighborhood}</p>
                        )}
                        {selectedVoter.city_name && (
                          <p className="text-gray-500">{selectedVoter.city_name}/{selectedVoter.state_code}</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Dados eleitorais */}
                {(selectedVoter.voter_registration || selectedVoter.electoral_zone) && (
                  <div className="border-t border-gray-100 pt-4">
                    <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Dados eleitorais</h4>
                    <div className="grid grid-cols-3 gap-3">
                      <MiniField label="Titulo" value={selectedVoter.voter_registration} />
                      <MiniField label="Zona" value={selectedVoter.electoral_zone} />
                      <MiniField label="Secao" value={selectedVoter.electoral_section} />
                    </div>
                  </div>
                )}

                {/* Origem */}
                <div className="border-t border-gray-100 pt-4">
                  <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Origem</h4>
                  <div className="space-y-2">
                    {selectedVoter.channel_label && <InfoRow label="Canal" value={selectedVoter.channel_label} />}
                    {selectedVoter.form_name && <InfoRow label="Formulario" value={selectedVoter.form_name} />}
                    {selectedVoter.leader_name && <InfoRow icon={<Crown className="w-3.5 h-3.5" />} label="Lideranca" value={selectedVoter.leader_name} />}
                    <InfoRow label="Cadastrado em" value={formatDateTime(selectedVoter.created_at)} />
                  </div>
                </div>

                {/* Observacoes */}
                {selectedVoter.notes && (
                  <div className="border-t border-gray-100 pt-4">
                    <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Observacoes</h4>
                    <p className="text-sm text-gray-600 whitespace-pre-wrap">{selectedVoter.notes}</p>
                  </div>
                )}
              </div>
            )}

            {/* Tab: Atividade */}
            {panelTab === 'atividade' && (
              <div className="p-6">
                {voterHistory.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-8">Nenhuma atividade registrada</p>
                ) : (
                  <div className="relative pl-6">
                    <div className="absolute left-[11px] top-2 bottom-2 w-px bg-brand-100" />
                    <div className="space-y-0">
                      {voterHistory.map(h => (
                        <div key={h.id} className="relative flex gap-4 py-3.5 first:pt-0 last:pb-0">
                          <div className="absolute -left-6 top-3.5 w-[22px] h-[22px] rounded-full border-2 border-white bg-brand-500 flex items-center justify-center">
                            <span className="text-white">{iconMap[h.action_icon] || <div className="w-2 h-2 rounded-full bg-white" />}</span>
                          </div>
                          <div className="flex-1 ml-2">
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <p className="text-sm font-medium text-gray-900">{h.action_label}</p>
                                {h.description && <p className="text-xs text-gray-500 mt-0.5">{h.description}</p>}
                              </div>
                              <span className="text-[11px] text-gray-400 whitespace-nowrap mt-0.5">{formatRelativeDate(h.created_at)}</span>
                            </div>
                            {(h.user_name || h.leader_name || h.channel_label) && (
                              <div className="flex flex-wrap gap-2 mt-1.5">
                                {h.user_name && <span className="text-[11px] text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">por {h.user_name}</span>}
                                {h.leader_name && <span className="text-[11px] text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full flex items-center gap-1"><Crown className="w-2.5 h-2.5" />{h.leader_name}</span>}
                                {h.channel_label && <span className="text-[11px] text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">{h.channel_label}</span>}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Tab: Campanhas */}
            {panelTab === 'campanhas' && (
              <div className="p-6">
                {voterCampaigns.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-8">Nao participou de nenhuma campanha</p>
                ) : (
                  <div className="space-y-3">
                    {voterCampaigns.map(c => (
                      <div key={c.id} className="flex items-center justify-between p-3 rounded-xl border border-gray-200">
                        <div className="flex items-center gap-3">
                          <div className={clsx('w-9 h-9 rounded-lg flex items-center justify-center', c.is_active_campaign ? 'bg-brand-50 text-brand-500' : 'bg-gray-100 text-gray-400')}>
                            <Flag className="w-4 h-4" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-800">{c.name}</span>
                              {c.is_active_campaign && <span className="text-[10px] font-bold text-brand-500 bg-brand-50 px-1.5 py-0.5 rounded">ATIVA</span>}
                            </div>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {c.election_year} &middot; Engajado em {formatDate(c.engaged_at)}
                            </p>
                          </div>
                        </div>
                        <Badge variant={c.engagement_active ? 'success' : 'gray'} size="sm" dot>
                          {c.engagement_active ? 'Ativo' : 'Inativo'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ) : null}
      </SlidePanel>
    </div>
  )
}

function InfoRow({ icon, label, value }: { icon?: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-center gap-3">
      {icon && <span className="text-gray-400">{icon}</span>}
      <span className="text-xs text-gray-400 w-24 flex-shrink-0">{label}</span>
      <span className="text-sm text-gray-800">{value}</span>
    </div>
  )
}

function MiniField({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <p className="text-xs text-gray-400 mb-0.5">{label}</p>
      <p className={clsx('text-sm', value ? 'text-gray-800' : 'text-gray-300')}>{value || '—'}</p>
    </div>
  )
}
